import React, { useState, useEffect, useMemo } from 'react';
import { publicApi, blogCategoryApi, getMediaUrl } from '../services/api';
import '../styles/BlogPage.css';

const DEFAULT_FALLBACK_IMAGE = '/assets/images/PsychMitra.png';

function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBlogModal, setSelectedBlogModal] = useState(null);

  const [categories, setCategories] = useState([
    'All',
    'Mental Health',
    'Anxiety & Stress',
    'Mindfulness',
    'Relationships',
    'Self-Care',
    'Career & Work'
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogs();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await blogCategoryApi.getPublicCategories();
      if (res?.categories && Array.isArray(res.categories) && res.categories.length > 0) {
        const catNames = ['All', ...res.categories.map((c) => c.name)];
        setCategories(Array.from(new Set(catNames)));
      }
    } catch (err) {
      console.warn('Could not fetch dynamic blog categories:', err);
    }
  };

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await publicApi.getBlogs({ limit: 50 });
      const serverBlogs = response?.blogs || response?.data || (Array.isArray(response) ? response : []);

      if (serverBlogs.length > 0) {
        const mapped = serverBlogs.map((b, idx) => {
          const authorObj = b.createdBy || {};
          const authorName = typeof authorObj === 'object' ? (authorObj.name || 'Mental Health Professional') : 'Mental Health Professional';

          const dateObj = b.createdAt ? new Date(b.createdAt) : new Date();
          const formattedDate = dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

          const rawImage = (typeof b.featuredImage === 'object' && b.featuredImage?.url)
            ? b.featuredImage.url
            : (b.featuredImage || b.image || b.coverImage || '');
          const resolvedImg = rawImage ? getMediaUrl(rawImage) : DEFAULT_FALLBACK_IMAGE;

          return {
            id: b._id || b.id || `blog-${idx}`,
            title: b.title || 'Mental Health Insight',
            author: authorName,
            date: formattedDate,
            category: b.category || 'Mental Health',
            image: resolvedImg || DEFAULT_FALLBACK_IMAGE,
            excerpt: b.excerpt || (b.content ? b.content.slice(0, 120) + '...' : 'Clinical insights and self-care strategies.'),
            content: b.content || b.excerpt || 'Full article content unavailable.'
          };
        });

        setBlogs(mapped);
      } else {
        setBlogs([]);
      }
    } catch (err) {
      console.warn('API error fetching blogs:', err);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter blogs based on search query & selected category
  const filteredBlogs = useMemo(() => {
    return blogs.filter((blog) => {
      const matchesCategory =
        selectedCategory === 'All' ||
        blog.category.toLowerCase().trim() === selectedCategory.toLowerCase().trim();

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        blog.title.toLowerCase().includes(q) ||
        blog.author.toLowerCase().includes(q) ||
        blog.excerpt.toLowerCase().includes(q) ||
        blog.category.toLowerCase().includes(q);

      return matchesCategory && matchesSearch;
    });
  }, [blogs, selectedCategory, searchQuery]);

  return (
    <div className="pm-blog-page">
      {/* Background Mesh */}
      <div className="pm-blog-glow-mesh" aria-hidden="true">
        <div className="pm-blog-orb-1"></div>
        <div className="pm-blog-orb-2"></div>
      </div>

      {/* Minimal Header (No Description) */}
      <header className="pm-blog-hero">
        <div className="pm-blog-hero-inner">
          <h1 className="pm-blog-main-title">
            Blog & <span className="pm-blog-gradient">Insights</span>
          </h1>
        </div>
      </header>

      <main className="pm-blog-container">
        {/* Controls: Search + Categories */}
        <div className="pm-blog-controls">
          <div className="pm-blog-search-bar">
            <i className="fas fa-search pm-blog-search-icon" aria-hidden="true"></i>
            <input
              type="text"
              className="pm-blog-search-input"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search articles"
            />
            {searchQuery && (
              <button
                className="pm-blog-clear-btn"
                onClick={() => setSearchQuery('')}
                title="Clear"
              >
                ✕
              </button>
            )}
          </div>

          <div className="pm-blog-cats">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`pm-blog-cat-btn ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b', fontWeight: 600 }}>
            Loading articles...
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredBlogs.length === 0 && (
          <div className="pm-blog-empty">
            <h3 style={{ fontSize: '18px', color: '#0f172a', marginBottom: '8px' }}>No articles found</h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>Try a different keyword or reset filters.</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
              className="pm-blog-cat-btn active"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Uniform Cards Grid */}
        {!loading && filteredBlogs.length > 0 && (
          <div className="pm-blog-grid">
            {filteredBlogs.map((blog) => (
              <article key={blog.id} className="pm-blog-card">
                <div
                  className="pm-blog-card-img-wrap"
                  onClick={() => setSelectedBlogModal(blog)}
                >
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className={`pm-blog-card-img ${blog.image === DEFAULT_FALLBACK_IMAGE ? 'is-fallback-logo' : ''}`}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_FALLBACK_IMAGE;
                      e.currentTarget.classList.add('is-fallback-logo');
                    }}
                  />
                  <span className="pm-blog-card-cat-badge">{blog.category}</span>
                </div>

                <div className="pm-blog-card-content">
                  <div>
                    <div className="pm-blog-card-date">
                      <i className="far fa-calendar-alt"></i> {blog.date}
                    </div>

                    <h2
                      className="pm-blog-card-title"
                      onClick={() => setSelectedBlogModal(blog)}
                    >
                      {blog.title}
                    </h2>

                    <p className="pm-blog-card-excerpt">
                      {blog.excerpt}
                    </p>
                  </div>

                  <div className="pm-blog-card-footer">
                    <div className="pm-blog-card-author">
                      <span>By <strong>{blog.author}</strong></span>
                    </div>

                    <button
                      className="pm-blog-read-link"
                      onClick={() => setSelectedBlogModal(blog)}
                    >
                      Read More <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Clean Article Reader Modal */}
      {selectedBlogModal && (
        <div
          className="pm-blog-modal-overlay"
          onClick={() => setSelectedBlogModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="pm-blog-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pm-blog-modal-topbar">
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#7c4bb9', textTransform: 'uppercase' }}>
                {selectedBlogModal.category}
              </span>
              <button
                className="pm-blog-modal-close"
                onClick={() => setSelectedBlogModal(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <img
              src={selectedBlogModal.image}
              alt={selectedBlogModal.title}
              className={`pm-blog-modal-img ${selectedBlogModal.image === DEFAULT_FALLBACK_IMAGE ? 'is-fallback-logo' : ''}`}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_FALLBACK_IMAGE;
                e.currentTarget.classList.add('is-fallback-logo');
              }}
            />

            <div className="pm-blog-modal-body">
              <h2 className="pm-blog-modal-title">{selectedBlogModal.title}</h2>

              <div className="pm-blog-modal-meta">
                <span>By <strong>{selectedBlogModal.author}</strong></span>
                <span>•</span>
                <span>{selectedBlogModal.date}</span>
              </div>

              <div className="pm-blog-modal-excerpt">
                {selectedBlogModal.excerpt}
              </div>

              <div className="pm-blog-modal-content">
                {selectedBlogModal.content.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>

              <div className="pm-blog-modal-footer">
                <button
                  className="pm-blog-cat-btn active"
                  onClick={() => setSelectedBlogModal(null)}
                >
                  Close Article
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogPage;
